import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { alpha, Box, Button, Chip, CircularProgress, Link, Stack, Typography } from '@mui/material';
import useBundleTranslation from 'i18n';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import FormComponent from 'components/common/form/Form';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import {
    GenerationContextRow,
    GenerationDetails,
    GenerationResult,
    GenerationToolCall,
    generationHistoryAPI,
} from 'api/editor/generation-history';

const NS = 'app/editor/element/extreport';

interface Props {
    handleHidePopup: () => void;
    componentProps?: { [key: string]: string | number };
    popupConfig?: { title?: string };
}

const blockSx = {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    bgcolor: 'background.default',
    px: 1.75,
    py: 1.5,
    mb: 1.75,
    fontSize: 13,
    lineHeight: 1.55,
};

const preSx = {
    m: 0,
    mt: 0.5,
    px: 1,
    py: 0.75,
    fontFamily: 'monospace',
    fontSize: 11,
    lineHeight: 1.45,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    maxHeight: 160,
    overflowY: 'auto',
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 0.5,
};

function BlockTitle({ children, flag }: { children: React.ReactNode; flag?: React.ReactNode }) {
    return (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{children}</Typography>
            {flag && <Box sx={{ ml: 'auto !important' }}>{flag}</Box>}
        </Stack>
    );
}

function StatusChip({ details }: { details: GenerationDetails }) {
    const color =
        details.status_key === 'generated' ? 'success' : details.status_key === 'failed' ? 'error' : 'default';

    return <Chip size="small" label={details.status_label} color={color} data-test="gh_detail_status" />;
}

/** The run's static facts: a fixed set, so it stays one line. Variable values live in the Context section. */
function StaticLine({ details, t }: { details: GenerationDetails; t: (key: string) => string }) {
    const item = (key: string, value: React.ReactNode) => (
        <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
            <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>
                {t(key)}
            </Box>
            {value}
        </Box>
    );
    const trigger = details.trigger.burst_name ? (
        <>
            {details.trigger.label} —{' '}
            {details.trigger.burst_url ? (
                <Link href={details.trigger.burst_url} target="_blank" rel="noopener" data-test="gh_detail_burst_link">
                    {details.trigger.burst_name}
                </Link>
            ) : (
                details.trigger.burst_name
            )}
        </>
    ) : (
        details.trigger.label
    );

    return (
        <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            columnGap={2.25}
            rowGap={0.75}
            sx={{ mb: 2, fontSize: 12 }}
        >
            <StatusChip details={details} />
            {item('concierge_gh_detail_attempted', details.attempted)}
            {item('concierge_gh_detail_trigger', trigger)}
            {details.user_login && item('concierge_gh_detail_for', details.user_login)}
        </Stack>
    );
}

/** Key/value grid: keys stay on one line, values wrap, and the box scrolls in place past ~5 rows. */
function ContextGrid({ rows }: { rows: GenerationContextRow[] }) {
    return (
        <Box
            data-test="gh_detail_context_grid"
            sx={{
                display: 'grid',
                gridTemplateColumns: 'max-content minmax(0, 1fr)',
                columnGap: 1.75,
                rowGap: 0.625,
                maxHeight: 150,
                overflowY: 'auto',
                px: 1.25,
                py: 1,
                fontSize: 12,
                lineHeight: 1.5,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 0.5,
            }}
        >
            {rows.map((row, index) => (
                <React.Fragment key={`${row.key}-${index}`}>
                    <Box sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{row.key}</Box>
                    <Box sx={{ minWidth: 0, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>{row.value}</Box>
                </React.Fragment>
            ))}
        </Box>
    );
}

function ToolCalls({ calls, details }: { calls: GenerationToolCall[]; details: GenerationDetails }) {
    if (calls.length === 0) {
        return <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{details.returned_empty}</Typography>;
    }

    return (
        <Stack spacing={1}>
            {calls.map((call, index) => (
                <Box key={`${call.tool}-${index}`}>
                    <Typography sx={{ fontSize: 13 }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                            {call.tool}
                        </Box>
                        {' · '}
                        <Box component="span" sx={{ color: call.status === 'error' ? 'error.main' : 'text.secondary' }}>
                            {call.outcome_label}
                        </Box>
                    </Typography>
                    {call.request && (
                        <Box component="pre" sx={preSx}>
                            {call.request}
                        </Box>
                    )}
                    {call.response && (
                        <Box component="pre" sx={preSx}>
                            {call.response}
                        </Box>
                    )}
                </Box>
            ))}
        </Stack>
    );
}

function SignificanceBlock({ details, t }: { details: GenerationDetails; t: (key: string) => string }) {
    const needsReconstruct = !details.evaluation.text && !!details.evaluation.url;
    const { data: reconstructed, isLoading } = useQuery({
        queryKey: ['aiInsightGenerationHistory', 'evaluation', details.id],
        queryFn: () => generationHistoryAPI.evaluation(details.evaluation.url as string),
        enabled: needsReconstruct,
        retry: false,
        staleTime: Infinity,
    });
    const text = details.evaluation.text ?? reconstructed;
    const flag = (
        <Chip
            size="small"
            variant="outlined"
            color={details.significant ? 'success' : 'default'}
            label={t(details.significant ? 'concierge_gh_detail_significant' : 'concierge_gh_detail_not_significant')}
            data-test="gh_detail_significance_flag"
        />
    );

    return (
        <Box sx={blockSx} data-test="gh_detail_significance">
            <BlockTitle flag={flag}>{t('concierge_gh_detail_significance')}</BlockTitle>
            {needsReconstruct && isLoading ? (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ color: 'text.secondary' }}>
                    <CircularProgress size={14} thickness={5} />
                    <span>{t('concierge_gh_detail_reconstructing')}</span>
                </Stack>
            ) : text ? (
                <Box sx={{ whiteSpace: 'pre-wrap' }}>{text}</Box>
            ) : (
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                    {t('concierge_gh_detail_evaluation_unavailable')}
                </Typography>
            )}
        </Box>
    );
}

function ResultBlock({ result, t }: { result: GenerationResult; t: (key: string) => string }) {
    const failed = result.kind === 'failed';

    return (
        <Box
            data-test={`gh_detail_result_${result.kind}`}
            sx={{
                ...blockSx,
                mb: 0,
                ...(failed && {
                    borderColor: 'error.main',
                    bgcolor: (theme: any) => alpha(theme.palette.error.main, 0.04),
                }),
            }}
        >
            <BlockTitle>{result.title}</BlockTitle>
            <Box sx={{ whiteSpace: 'pre-wrap' }}>{result.text}</Box>
            {result.suggested_fix && (
                <Box sx={{ mt: 0.75, color: 'text.secondary' }}>
                    {t('concierge_gh_detail_suggested_fix')} {result.suggested_fix}
                </Box>
            )}
        </Box>
    );
}

export default function AiInsightGenerationDetails({ handleHidePopup, componentProps, popupConfig }: Props) {
    const { t } = useBundleTranslation(NS);
    const { t: tMain } = useBundleTranslation('main');
    const elementId = Number(componentProps?.elementId ?? 0);
    const logId = Number(componentProps?.logId ?? 0);
    const [editing, setEditing] = useState(false);

    const { data, isLoading, isError } = useQuery({
        queryKey: ['aiInsightGenerationHistory', 'details', elementId, logId],
        queryFn: () => generationHistoryAPI.details(elementId, logId),
        retry: false,
    });

    const editUrl = data?.insight.edit_url ?? null;
    if (editing && editUrl) {
        // The same slide-out the Insights list opens for a row, so both entry points edit alike.
        return (
            <FormComponent
                isPopup
                popupType="edit"
                pk={editUrl}
                uid={`ai_insight_${data?.insight.id}`}
                settingsUrl={editUrl}
                onHide={handleHidePopup}
                afterSave={handleHidePopup}
                popupSettings={{ asidePanel: true, asidePanelHideTabs: true }}
            />
        );
    }

    const settings: PopupSettings = {
        title: data?.insight.name ?? popupConfig?.title ?? String(componentProps?.title ?? ''),
        textOK: tMain('close'),
        needTranslation: false,
        noCancel: true,
        maxWidth: 'popupLg',
        dataTestOkButton: 'gh_detail_close_button',
    };
    const additionalButtons = editUrl
        ? [
              <Button
                  key="edit-insight"
                  variant="outlined"
                  onClick={() => setEditing(true)}
                  data-test="gh_detail_edit_insight_button"
              >
                  {t('concierge_gh_detail_edit_insight')}
              </Button>,
          ]
        : undefined;

    return (
        <Popup
            open
            settings={settings}
            maxWidth="popupLg"
            onHide={handleHidePopup}
            onConfirm={handleHidePopup}
            additionalButtons={additionalButtons}
        >
            {isLoading && <LoadingPlaceholder />}
            {isError && (
                <Typography sx={{ color: 'text.secondary' }} data-test="gh_detail_not_found">
                    {t('concierge_gh_detail_not_found')}
                </Typography>
            )}
            {data && (
                <Box data-test="gh_detail_body">
                    <StaticLine details={data} t={t} />
                    <Box sx={blockSx} data-test="gh_detail_prompt">
                        <BlockTitle>{t('concierge_gh_detail_prompt')}</BlockTitle>
                        <Box sx={{ whiteSpace: 'pre-wrap' }}>{data.prompt}</Box>
                        {data.context_rows.length > 0 && (
                            <Box sx={{ mt: 1.5, pt: 1.25, borderTop: '1px dashed', borderColor: 'divider' }}>
                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        fontWeight: 600,
                                        letterSpacing: '.03em',
                                        textTransform: 'uppercase',
                                        color: 'text.secondary',
                                        mb: 0.75,
                                    }}
                                >
                                    {t('concierge_gh_detail_context')}
                                </Typography>
                                <ContextGrid rows={data.context_rows} />
                            </Box>
                        )}
                    </Box>
                    {data.is_conditional && (
                        <Box sx={blockSx} data-test="gh_detail_returned">
                            <BlockTitle>{t('concierge_gh_detail_returned')}</BlockTitle>
                            <ToolCalls calls={data.returned} details={data} />
                            <Typography sx={{ mt: 1, fontSize: 12, color: 'text.secondary' }}>
                                {data.returned_help}
                            </Typography>
                        </Box>
                    )}
                    {data.is_conditional && data.status_key !== 'failed' && <SignificanceBlock details={data} t={t} />}
                    <ResultBlock result={data.result} t={t} />
                </Box>
            )}
        </Popup>
    );
}
